import React, { useState } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import {
    InteractionRequiredAuthError,
    InteractionRequiredAuthErrorCodes
} from "@azure/msal-browser";
import { BrowserRouter as Router, Routes, Route } from "react-router";
import GalleryPage from "./pages/GalleryPage";
import CountryListPage from "./pages/CountryListPage";
import ArtworkPage from "./pages/ArtworkDetails/ArtworkPage";
import ClientSettingsPage from "./pages/ClientSettingsPage";
import ArtworkManagement from "./components/ArtworkManagement/ArtworkManagement";
import HomePage from "./pages/HomePage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import UserManagementPage from "./pages/UserManagementPage";
import './App.css';
import { UserProvider } from "./components/UserContext";
import { UserBootstrap } from './components/UserBootstrap';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { CurrencyProvider } from './components/CurrencyContext';
import { ReferenceDataProvider } from './components/ReferenceDataContext';
import { FilterProvider } from './components/Filter/ArtworkFilterContext';
import CategorySettingsPage from './admin/CategorySettingsPage';
import LocationSettingsPage from './admin/LocationSettingsPage';
import ClientArtists from './components/ArtistManagement/ClientArtists';
//import EventsManager from './components/Events/EventsManager';
import DiaryPage from './components/DiaryManagement/DiaryPage';
import PortfolioManager from './components/Portfolios/PortfolioManager';
import { AppLayout } from './AppLayout';
import UserProfileDialog from "./components/UserProfile/UserProfilePage";
import './styles/globals.css';
import './styles/components.css';
import ScrollToTop from './functions/ScrollToTop';
import PortfolioDashboard from './pages/Dashboard/PortfolioDashboard';
import DashboardPage from "./components/Dashboard/DashboardPage";

import { RequirePermission } from "./auth/RequirePermission";
import ForbiddenPage from "./pages/ForbiddenPage";

//Artwork Pages
import MovementsPage from "./components/Movements/MovementsPage";

//Admin Pages
import AdminHomePage from "./admin/AdminHomePage";
import AdminSourcesPage from "./admin/AdminSourcesPage";
import AdminArtworkStatusesPage from "./admin/AdminArtworkStatusesPage";
import AdminNoteTypesPage from "./admin/AdminNoteTypesPage";
import LicenseManagementPage from './components/LicenseManagement/LicenseManagementPage';
import AdminTenantPage from "./admin/AdminTenantPage";
import AdminOrganisationsPage from "./admin/AdminOrganisationsPage";
import AdminAuditPage from "./components/Audit/AdminAuditPage";
import DiaryMaintenancePage from "./admin/DiaryMaintenancePage";

//Payments
import PaymentsPage from "./pages/PaymentsPage";

function RootProviders({ children }: { children: React.ReactNode }) {

    const { instance, accounts } = useMsal();

    const getAccessToken = React.useCallback(async (): Promise<string | null> => {

        const account =
            instance.getActiveAccount() ??
            accounts[0];

        if (!account) {
            return null;
        }

        try {
            const res = await instance.acquireTokenSilent({
                ...loginRequest,
                account
            });

            return res.accessToken;
        }
        catch (error) {

            // Important:
            // Don't start loginRedirect/acquireTokenRedirect here.
            // Allow UserBootstrap to handle interactive recovery.
            if (error instanceof InteractionRequiredAuthError) {
                throw error;
            }

            throw error;
        }

    }, [instance, accounts]);

    return (
        <UserProvider getAccessToken={getAccessToken}>
            {children}
        </UserProvider>
    );
}

export default function App() {
    const [refreshGallery, setRefreshGallery] = useState(false);

    //User
    const [profileOpen, setProfileOpen] = useState(false);
    const handleCloseProfile = () => {
        setProfileOpen(false);
    };

    return (
        <>
            <ScrollToTop />
            <Routes>

                <Route path="/accept-invite/:inviteId" element={<AcceptInvitePage />} />
            </Routes>
            <AuthenticatedTemplate>
                <RootProviders>
                    <UserBootstrap blockUntilReady>
                        <ReferenceDataProvider>
                            <CurrencyProvider>
                                <FilterProvider>
                                    <AppLayout>
                                        <div style={{ padding: "10px" }}>
                                                <Routes>
                                                <Route path="/" element={<HomePage />} />
                                                <Route path="/forbidden" element={<ForbiddenPage />} />
                                                <Route path="/gallery" element={<GalleryPage refreshTrigger={refreshGallery} />} />
                                                <Route path="/countries" element={<CountryListPage />} />
                                                <Route path="/artwork" element={<ArtworkManagement isOpen={true} />} />
                                                <Route path="/artwork/:id" element={<ArtworkPage />} />
                                                <Route path="/movements" element={<MovementsPage />} />
                                                <Route path="/settings/clients" element={<ClientSettingsPage />} />
                                                <Route path="/settings/locations" element={<LocationSettingsPage />} />
                                                
                                                <Route path="/settings/users" element={<UserManagementPage />} />
                                                <Route path="/settings/categories" element={<CategorySettingsPage />} />
                                                <Route path="/settings/artists" element={<ClientArtists />} />
                                                <Route path="/settings/profile" element={<UserProfileDialog open={profileOpen} onClose={handleCloseProfile} />} />
                                                <Route path="/events/view" element={<DiaryPage />} />
                                                <Route path="/portfolios" element={<PortfolioManager />} />
                                                <Route path="/dashboard" element={<DashboardPage />} />
                                                <Route path="/admin" element={<AdminHomePage />} />
                                                <Route
                                                    path="/admin/organisations"
                                                    element={
                                                        <RequirePermission perm="admin:organisations">
                                                            <AdminOrganisationsPage />
                                                        </RequirePermission>
                                                    }
                                                />
                                                <Route path="/admin/sources" element={
                                                    <RequirePermission perm="admin:sources">
                                                        <AdminSourcesPage />
                                                    </RequirePermission>
                                                } />
                                                <Route
                                                    path="/admin/statuses"
                                                    element={
                                                        <RequirePermission perm="admin:statuses">
                                                            <AdminArtworkStatusesPage />
                                                        </RequirePermission>
                                                    }
                                                />
                                                <Route
                                                    path="/admin/note-types"
                                                    element={
                                                        <RequirePermission perm="admin:note-types">
                                                            <AdminNoteTypesPage />
                                                        </RequirePermission>
                                                    }
                                                />
                                                <Route
                                                    path="/admin/tenant"
                                                    element={<LicenseManagementPage />  }
                                                />
                                                <Route path="/admin/sysAdmin" element={<AdminTenantPage />} />
                                                <Route path="/admin/repair-diary" element={<DiaryMaintenancePage /> } />
                                                <Route
                                                    path="/admin/audit"
                                                    element={<AdminAuditPage />}
                                                />
                                                <Route
                                                    path="/payments"
                                                    element={
                                                        <PaymentsPage />
                                                    }
                                                />
                                                <Route path="/privacy" element={<PrivacyPage />} />
                                                <Route path="/terms" element={<TermsPage />} />
                                            </Routes>
                                           </div>
                                       </AppLayout>
                                </FilterProvider>
                            </CurrencyProvider>
                        </ReferenceDataProvider>
                    </UserBootstrap>
                </RootProviders>
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <Routes>
                    {/* Public */}
                    <Route path="/" element={<HomePage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    
                </Routes>
            </UnauthenticatedTemplate>
        </>
    );
}
